class CustomizableReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reviews-track]');
    this.progressBar = this.querySelector('[data-reviews-progress-bar]');
    this.previousButton = this.querySelector('[data-reviews-prev]');
    this.nextButton = this.querySelector('[data-reviews-next]');

    if (!this.track || !this.progressBar) return;

    this.onScroll = this.updateProgress.bind(this);
    this.onResize = this.updateProgress.bind(this);
    this.onPrevious = () => this.scrollByCard(-1);
    this.onNext = () => this.scrollByCard(1);
    this.onPointerDown = this.startDrag.bind(this);
    this.onPointerMove = this.drag.bind(this);
    this.onPointerUp = this.endDrag.bind(this);
    this.onClick = this.stopClickAfterDrag.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('lostpointercapture', this.onPointerUp);
    this.track.addEventListener('click', this.onClick, true);
    window.addEventListener('resize', this.onResize);
    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);

    this.updateProgress();
    requestAnimationFrame(() => this.updateProgress());
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('lostpointercapture', this.onPointerUp);
    this.track?.removeEventListener('click', this.onClick, true);
    window.removeEventListener('resize', this.onResize);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
  }

  scrollByCard(direction) {
    const firstCard = this.track.querySelector('[data-review-card]');
    if (!firstCard) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
    const amount = firstCard.getBoundingClientRect().width + gap;

    this.track.scrollBy({
      left: direction * amount,
      behavior: 'smooth'
    });
  }

  startDrag(event) {
    if (event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0)) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(event.pointerId);
  }

  drag(event) {
    if (!this.isDragging) return;

    const deltaX = event.clientX - this.dragStartX;

    if (Math.abs(deltaX) > 3) {
      this.hasDragged = true;
    }

    if (this.hasDragged) {
      event.preventDefault();
      this.track.scrollLeft = this.dragStartScrollLeft - deltaX;
      this.updateProgress();
    }
  }

  endDrag(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    if (this.track.hasPointerCapture?.(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }

    requestAnimationFrame(() => this.updateProgress());
  }

  stopClickAfterDrag(event) {
    if (!this.hasDragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  }

  updateProgress() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const canScroll = maxScroll > 1;
    const visibleRatio = canScroll ? this.track.clientWidth / this.track.scrollWidth : 1;
    const initialWidth = Math.max(visibleRatio * 100, 8);
    const progress = canScroll ? Math.max(0, Math.min(1, this.track.scrollLeft / maxScroll)) : 1;
    const barWidth = initialWidth + (100 - initialWidth) * progress;

    this.progressBar.style.width = `${barWidth}%`;
    this.progressBar.style.left = '0';

    if (this.previousButton) {
      this.previousButton.disabled = !canScroll || this.track.scrollLeft <= 1;
    }

    if (this.nextButton) {
      this.nextButton.disabled = !canScroll || this.track.scrollLeft >= maxScroll - 1;
    }
  }
}

if (!customElements.get('customizable-reviews')) {
  customElements.define('customizable-reviews', CustomizableReviews);
}
